import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Save, RefreshCw, Loader2, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { authHeaders, clearToken, getToken } from "../lib/adminAuth";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const nav = useNavigate();
  const [groups, setGroups] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Auth check on mount
  useEffect(() => {
    if (!getToken()) {
      nav("/admin", { replace: true });
      return;
    }
    loadCensus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCensus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/census`);
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json();
      setGroups(data.groups || []);
      setUpdatedAt(data.updated_at || null);
    } catch (e) {
      toast.error("Could not load census.");
    } finally {
      setLoading(false);
    }
  };

  const updateCount = (gi, ii, value) => {
    const v = value === "" ? "" : Math.max(0, parseInt(value, 10) || 0);
    setGroups((prev) => {
      const next = prev.map((g) => ({ ...g, items: g.items.map((it) => ({ ...it })) }));
      next[gi].items[ii].count = v === "" ? 0 : v;
      return next;
    });
  };

  const updateLabel = (gi, ii, value) => {
    setGroups((prev) => {
      const next = prev.map((g) => ({ ...g, items: g.items.map((it) => ({ ...it })) }));
      next[gi].items[ii].label = value;
      return next;
    });
  };

  const addRow = (gi) => {
    setGroups((prev) => {
      const next = prev.map((g) => ({ ...g, items: g.items.map((it) => ({ ...it })) }));
      next[gi].items.push({ label: "New animal", count: 0, soft: false });
      return next;
    });
  };

  const removeRow = (gi, ii) => {
    setGroups((prev) => {
      const next = prev.map((g) => ({ ...g, items: g.items.map((it) => ({ ...it })) }));
      next[gi].items.splice(ii, 1);
      return next;
    });
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/census`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ groups }),
      });
      if (res.status === 401) {
        clearToken();
        toast.error("Your session expired. Please sign in again.");
        nav("/admin", { replace: true });
        return;
      }
      if (!res.ok) {
        toast.error("Could not save changes.");
        return;
      }
      const data = await res.json();
      setUpdatedAt(data.updated_at);
      toast.success("Census updated. Visible on the website immediately.");
    } catch (e) {
      toast.error("Network error while saving.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    clearToken();
    toast.success("Signed out.");
    nav("/admin", { replace: true });
  };

  const grand =
    groups?.reduce(
      (sum, g) => sum + g.items.reduce((s, it) => s + (Number(it.count) || 0), 0),
      0
    ) || 0;

  return (
    <div data-testid="admin-dashboard-page" className="min-h-[100svh] bg-[#F4F1EA] text-[#1A2E24]">
      {/* Top bar */}
      <header className="bg-[#0F2A22] text-[#FDFBF7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-white overflow-hidden flex items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_cow-rescue-hospital/artifacts/883uiflg_image.png"
                alt="Hastkesar Panjrapole logo"
                className="w-full h-full object-contain p-0.5"
              />
            </span>
            <div className="leading-tight">
              <div className="font-display text-[#FDFBF7]">Hastkesar Caretaker</div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
                Daily Census Editor
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-xs text-[#FDFBF7]/70 hover:text-[#D4AF37] px-4 py-2 rounded-full"
              data-testid="admin-view-site"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-xs bg-[#FDFBF7]/10 hover:bg-[#FDFBF7]/20 text-[#FDFBF7] px-4 py-2 rounded-full transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#4A5D53] mb-3">
              Today's living family
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#1A2E24] leading-[1.05]">
              {grand.toLocaleString()}{" "}
              <span className="italic text-[#1B4D3E]">lives</span>
            </h1>
            {updatedAt && (
              <div className="mt-3 text-xs text-[#7A8C81]">
                Last saved: {new Date(updatedAt).toLocaleString()}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadCensus}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 border border-[#1B4D3E]/20 hover:bg-[#1B4D3E]/5 text-[#1B4D3E] px-5 py-3 rounded-full text-sm font-medium transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Reload
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              data-testid="admin-save-btn"
              className="inline-flex items-center gap-2 bg-[#1B4D3E] hover:bg-[#143C30] text-[#FDFBF7] px-6 py-3 rounded-full text-sm font-semibold transition-all disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {loading || !groups ? (
          <div className="flex items-center justify-center py-32 text-[#7A8C81]">
            <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading census…
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {groups.map((g, gi) => {
              const subtotal = g.items.reduce(
                (s, it) => s + (Number(it.count) || 0),
                0
              );
              return (
                <section
                  key={g.key || gi}
                  data-testid={`admin-group-${g.key || gi}`}
                  className="bg-[#FDFBF7] border border-[#E3DECE] rounded-2xl p-6"
                >
                  <div className="flex items-baseline justify-between mb-5">
                    <div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-semibold">
                        {g.title}
                      </div>
                      <div className="mt-1 text-xs text-[#7A8C81]">
                        Subtotal:{" "}
                        <span className="font-display text-[#1B4D3E] text-base">
                          {subtotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-3">
                    {g.items.map((it, ii) => (
                      <li
                        key={ii}
                        className="grid grid-cols-12 gap-3 items-center"
                      >
                        <input
                          type="text"
                          value={it.label}
                          onChange={(e) => updateLabel(gi, ii, e.target.value)}
                          data-testid={`admin-label-${g.key}-${ii}`}
                          className="col-span-7 bg-[#F4F1EA] border border-[#E3DECE] focus:border-[#1B4D3E] outline-none rounded-lg px-3 py-2 text-sm text-[#1A2E24]"
                        />
                        <input
                          type="number"
                          min="0"
                          value={it.count}
                          onChange={(e) => updateCount(gi, ii, e.target.value)}
                          data-testid={`admin-count-${g.key}-${ii}`}
                          className="col-span-4 bg-[#FDFBF7] border border-[#E3DECE] focus:border-[#1B4D3E] outline-none rounded-lg px-3 py-2 text-sm font-display text-[#1B4D3E] tabular-nums text-right"
                        />
                        <button
                          onClick={() => removeRow(gi, ii)}
                          data-testid={`admin-remove-${g.key}-${ii}`}
                          aria-label="Remove row"
                          className="col-span-1 w-8 h-8 rounded-full text-[#7A8C81] hover:text-[#B5952F] hover:bg-[#F4F1EA] flex items-center justify-center transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => addRow(gi)}
                    data-testid={`admin-add-${g.key}`}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-dashed border-[#1B4D3E]/30 hover:bg-[#1B4D3E]/5 text-[#1B4D3E] px-4 py-2.5 rounded-lg text-xs tracking-[0.18em] uppercase transition-all"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add animal
                  </button>
                </section>
              );
            })}
          </div>
        )}

        <p className="mt-12 text-center text-[11px] text-[#7A8C81] tracking-wider uppercase">
          Changes go live on the public site as soon as you press Save.
        </p>
      </main>
    </div>
  );
}
